import { useQuery } from '@tanstack/react-query';

import { useEffect, useState } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';

import { useApiClient, useToastService, uuid } from '@@common';
import { DialogHeaderActions } from '@@components';

import { ChangeSetClient, UserInterfaceClient } from '../../../api/api';
import { Bundle, DEFAULT_CURRENCY_CODE, DEFAULT_PHONE_NUMBER_FORMAT } from '../Models/Bundle';

type CreateAppDialogProps = {
	open: boolean;
	setOpen: (open: boolean) => void;
	onSuccess?: () => void;
	defaultChangeSetId?: string | null;
};

const emptyForm = {
	name: '',
	description: '',
	changeSetId: '',
};

export default function CreateAppDialog({ open, setOpen, onSuccess, defaultChangeSetId }: CreateAppDialogProps) {
	const [form, setForm] = useState(emptyForm);
	const [saving, setSaving] = useState(false);
	const [isMaximized, setIsMaximized] = useState(false);

	const toastService = useToastService();
	const client = useApiClient(UserInterfaceClient);
	const changeSetClient = useApiClient(ChangeSetClient);

	const { data: changeSetsData } = useQuery({
		queryKey: ['changeSets', 'all'],
		queryFn: () => changeSetClient.search(undefined, undefined, 0, 500, undefined, undefined),
		enabled: open,
	});

	useEffect(() => {
		if (open) {
			setForm({ ...emptyForm, changeSetId: defaultChangeSetId ?? '' });
		}
	}, [open, defaultChangeSetId]);

	const handleClose = () => {
		setOpen(false);
		setIsMaximized(false);
	};

	const handleSave = async () => {
		if (!form.name.trim()) {
			toastService.error('Name is required');
			return;
		}
		if (!form.changeSetId) {
			toastService.error('Change Set is required');
			return;
		}

		setSaving(true);
		try {
			const bundleDefinition: Bundle = {
				Id: uuid(),
				Name: form.name.trim(),
				Description: form.description.trim() || undefined,
				Themes: [{ Id: uuid(), Name: 'Default', IsDefault: true, Theme: {} }],
				Settings: {
					PhoneNumberFormat: DEFAULT_PHONE_NUMBER_FORMAT,
					CurrencyCode: DEFAULT_CURRENCY_CODE,
				},
				EmbeddedResources: [],
				SharedComponents: [],
				Views: [
					{
						Type: 'View',
						RequiresAuthentication: true,
						Id: uuid(),
						Title: 'Home',
						RoutePath: '/',
						Children: [],
						Menu: { Title: 'Home', IconName: 'Home' },
					},
				],
			};

			await client.create({ name: form.name.trim(), changeSetId: form.changeSetId, definition: JSON.stringify(bundleDefinition) });
			toastService.success(`App "${form.name.trim()}" created successfully`);
			handleClose();
			onSuccess?.();
		} catch (error) {
			console.error('Error creating bundle:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to create app: ${errorMessage}`);
		} finally {
			setSaving(false);
		}
	};

	const changeSets = changeSetsData?.items ?? [];

	const isFormValid = form.name.trim() !== '' && form.changeSetId !== '';

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMaximized}>
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<span style={{ flex: 1 }}>Create App</span>
				<DialogHeaderActions
					isMaximized={isMaximized}
					onToggleMaximized={() => setIsMaximized((value) => !value)}
					onClose={handleClose}
					closeDisabled={saving}
				/>
			</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					<TextField
						label="Name"
						required
						value={form.name}
						onChange={(e) => setForm({ ...form, name: e.target.value })}
						fullWidth
						variant="outlined"
						autoFocus
					/>
					<TextField
						label="Description"
						value={form.description}
						onChange={(e) => setForm({ ...form, description: e.target.value })}
						fullWidth
						variant="outlined"
						multiline
						minRows={3}
					/>
					<FormControl fullWidth required>
						<InputLabel id="changeset-select-label">Change Set</InputLabel>
						<Select
							labelId="changeset-select-label"
							value={form.changeSetId}
							label="Change Set"
							onChange={(e) => setForm({ ...form, changeSetId: e.target.value })}
						>
							{changeSets.map((cs) => (
								<MenuItem key={cs.id} value={cs.id}>
									{cs.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={saving}>
					Cancel
				</Button>
				<Button onClick={handleSave} variant="contained" disabled={saving || !isFormValid} loading={saving}>
					Create
				</Button>
			</DialogActions>
		</Dialog>
	);
}
